import {
    ConflictError,
    ForbiddenError,
    InternalServerError,
    InvalidRequestError,
    NotFoundError,
    SdkError,
    UnauthorizedError,
} from "./errors.ts";

export type BaseSdkClientOptions = {
    endpoint: string;
    routePrefix?: string;
    onUnauthorized?: () => void;
    timeout?: number;
    authToken?: string;
};

export type RequestOptions = {
    method: string;
    body?: unknown;
    headers?: Record<string, string>;
};

export const DEFAULT_API_ROUTE_PREFIX = "/v1";

export class BaseSdkClient {
    public readonly endpoint: string;
    protected readonly routePrefix: string;
    protected readonly onUnauthorized?: () => void;
    protected readonly timeout?: number;
    protected readonly authToken?: string;

    constructor(options?: BaseSdkClientOptions) {
        this.endpoint = options?.endpoint ?? "";
        this.routePrefix = options?.routePrefix ?? DEFAULT_API_ROUTE_PREFIX;
        this.onUnauthorized = options?.onUnauthorized;
        this.timeout = options?.timeout;
        this.authToken = options?.authToken;
    }

    private buildAuthHeaders = (
        headers: Record<string, string>,
    ): Record<string, string> => {
        if (this.authToken) {
            return {
                ...headers,
                "Authorization": `Bearer ${this.authToken}`,
            };
        }
        return headers;
    };

    private fetchWithTimeout = async (
        url: string,
        config: RequestInit,
    ): Promise<Response> => {
        const controller = new AbortController();
        let timeoutId: ReturnType<typeof setTimeout> | undefined;

        if (this.timeout) {
            timeoutId = setTimeout(() => controller.abort(), this.timeout);
        }

        try {
            return await fetch(url, {
                ...config,
                signal: controller.signal,
            });
        } finally {
            if (timeoutId !== undefined) {
                clearTimeout(timeoutId);
            }
        }
    };

    private handleErrorResponse = async (
        response: Response,
    ): Promise<never> => {
        if (response.status === 401 && this.onUnauthorized) {
            this.onUnauthorized();
        }

        let errorMessage = `Request failed (${response.status})`;
        let errorData: unknown;

        try {
            errorData = await response.json();
            if (
                errorData && typeof errorData === "object" &&
                "error" in errorData
            ) {
                errorMessage = (errorData as { error: string; }).error;
            }
        } catch {
            /* ignore */
        }

        switch (response.status) {
            case 400:
                throw new InvalidRequestError(errorMessage, errorData);
            case 401:
                throw new UnauthorizedError(errorMessage, errorData);
            case 403:
                throw new ForbiddenError(errorMessage, errorData);
            case 404:
                throw new NotFoundError(errorMessage, errorData);
            case 409:
                throw new ConflictError(errorMessage, errorData);
            case 500:
                throw new InternalServerError(errorMessage, errorData);
            default:
                throw new SdkError(errorMessage, response.status, errorData);
        }
    };

    public request = async <T>(
        operation: string,
        requestOptions: RequestOptions,
    ): Promise<T> => {
        const {
            method,
            body,
            headers = {},
        } = requestOptions;

        const config: RequestInit = {
            method,
            headers: this.buildAuthHeaders({
                ...headers,
            }),
            credentials: "include",
        };

        if (body) {
            (config.headers as Record<string, string>)["Content-Type"] =
                "application/json";
            config.body = JSON.stringify(body);
        }

        const fullRequestUrl = this.endpoint + this.routePrefix + operation;

        const response = await this.fetchWithTimeout(fullRequestUrl, config);

        if (!response.ok) {
            await this.handleErrorResponse(response);
        }

        // handle empty responses (e.g. 204 No Content)
        if (
            response.status === 204 ||
            response.headers.get("content-length") === "0"
        ) {
            return undefined as T;
        }

        return response.json();
    };

    public get = <T>(
        operation: string,
        params?: Record<
            string,
            string | number | boolean | string[] | undefined | null
        >,
    ): Promise<T> => {
        const requestOptions = {
            method: "GET",
        };

        if (!params) {
            return this.request<T>(operation, requestOptions);
        }

        const searchParams = new URLSearchParams();
        for (const [key, value] of Object.entries(params)) {
            if (value === undefined || value === null || value === "") {
                continue;
            }
            if (Array.isArray(value)) {
                for (const item of value) {
                    searchParams.append(key, item);
                }
            } else {
                searchParams.set(key, String(value));
            }
        }

        const queryString = searchParams.toString();
        return this.request<T>(
            operation + (queryString ? `?${queryString}` : ""),
            requestOptions,
        );
    };

    public post = <T>(operation: string, body?: unknown): Promise<T> => {
        return this.request<T>(operation, {
            method: "POST",
            body,
        });
    };

    public put = <T>(operation: string, body?: unknown): Promise<T> => {
        return this.request<T>(operation, {
            method: "PUT",
            body,
        });
    };

    public patch = <T>(operation: string, body?: unknown): Promise<T> => {
        return this.request<T>(operation, {
            method: "PATCH",
            body,
        });
    };

    public del = <T>(operation: string): Promise<T> => {
        return this.request<T>(operation, {
            method: "DELETE",
        });
    };

    // post JSON body, receive raw binary response (e.g. audio/image)
    public postForBuffer = async (
        operation: string,
        body: unknown,
    ): Promise<ArrayBuffer> => {
        const url = this.endpoint + this.routePrefix + operation;

        const config: RequestInit = {
            method: "POST",
            headers: this.buildAuthHeaders({
                "Content-Type": "application/json",
            }),
            body: JSON.stringify(body),
            credentials: "include",
        };

        const response = await this.fetchWithTimeout(url, config);

        if (!response.ok) {
            await this.handleErrorResponse(response);
        }

        return response.arrayBuffer();
    };

    public requestBinary = async <T>(
        operation: string,
        data: Blob | ArrayBuffer | Uint8Array,
    ): Promise<T> => {
        const url = this.endpoint + this.routePrefix + operation;
        const body =
            (data instanceof Uint8Array ? data.buffer : data) as BodyInit;

        const config: RequestInit = {
            method: "POST",
            headers: this.buildAuthHeaders({
                "Content-Type": "application/octet-stream",
            }),
            body,
            credentials: "include",
        };

        const response = await this.fetchWithTimeout(url, config);

        if (!response.ok) {
            await this.handleErrorResponse(response);
        }

        return await response.json() as T;
    };
}
